/**
 * HomeContributeCards.jsx — v1.0.0 Gate 5.5 Item 1
 *
 * Contribute section on Home tab. Three tappable cards:
 *   - Spot a Food Truck (+10)
 *   - Submit an Offer (+20)
 *   - Report an Update (+10)
 *
 * Renders at position 11 (between Community Picks and footer).
 */
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Gift, Flag, ChevronRight } from "lucide-react";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

const CARDS = [
  {
    id: "spot_truck",
    icon: Truck,
    title: "Spot a Food Truck",
    tagline: "I see a truck right now. Let the community know.",
    points: 10,
    accent: "from-orange-400 to-amber-500",
    iconBg: "bg-orange-100 text-orange-600",
    to: "/spot-truck",
    testId: "contribute-spot-truck",
    flag: "truck_features",
  },
  {
    id: "submit_offer",
    icon: Gift,
    title: "Submit an Offer",
    tagline: "Found a birthday deal not in the app. Add it.",
    points: 20,
    accent: "from-sky-400 to-indigo-500",
    iconBg: "bg-sky-100 text-sky-600",
    to: "/submit-offer",
    testId: "contribute-submit-offer",
  },
  {
    id: "report_update",
    icon: Flag,
    title: "Report an Update",
    tagline: "Something changed. Help us keep it accurate.",
    points: 10,
    accent: "from-emerald-400 to-teal-500",
    iconBg: "bg-emerald-100 text-emerald-600",
    to: "/report-update",
    testId: "contribute-report-update",
  },
];

export default function HomeContributeCards() {
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const visibleCards = CARDS.filter((c) => !c.flag || flags[c.flag]);
  if (visibleCards.length === 0) return null;

  return (
    <section className="px-4 mt-6 mb-4" data-testid="home-contribute-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold tracking-widest uppercase text-slate-500">
          Contribute
        </h3>
        <span className="text-[10px] text-slate-400">Earn points</span>
      </div>
      <div className="space-y-2">
        {visibleCards.map((c, i) => (
          <motion.button
            key={c.id}
            type="button"
            onClick={() => navigate(c.to)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="w-full flex items-center gap-3 p-3 bg-white rounded-2xl border border-slate-100 shadow-sm text-left hover:border-slate-200 transition-colors"
            data-testid={c.testId}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
              <c.icon className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-bold text-slate-800 truncate">{c.title}</p>
                <span
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r text-white ${c.accent}`}
                  data-testid={`${c.testId}-points`}
                >
                  +{c.points} pts
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{c.tagline}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
          </motion.button>
        ))}
      </div>
    </section>
  );
}
